import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { adminAuditApi } from '../../services/api';
import { NexusCard, NexusBadge } from '../../components/nexus-ui';
import { Shield, ChevronLeft, ChevronRight } from 'lucide-react';

const ACTION_COLORS = {
  login: 'default',
  branding_update: 'info',
  plan_upgrade: 'success',
  config_change: 'warning',
  upload: 'info',
  api_key_create: 'premium',
  api_key_revoke: 'error',
};

const AdminAuditLogs = () => {
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('');
  const limit = 30;

  const { data } = useQuery({
    queryKey: ['auditLogs', page, filter],
    queryFn: () => adminAuditApi.getLogs({ limit, skip: page * limit, action: filter || undefined }).then(r => r.data),
  });

  const logs = data?.logs || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const actions = ['login', 'branding_update', 'plan_upgrade', 'config_change', 'upload', 'api_key_create'];

  return (
    <div className="space-y-6" data-testid="admin-audit-logs">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-nexus-text-primary flex items-center gap-2">
            <Shield size={22} className="text-nexus-accent-mint" /> Audit Logs
          </h2>
          <p className="text-nexus-text-secondary text-sm mt-1">{total} total events</p>
        </div>
        <select
          value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(0); }}
          data-testid="audit-filter"
          className="bg-nexus-bg-tertiary border border-nexus-border-emphasized rounded-lg px-3 py-2 text-sm text-nexus-text-primary focus:outline-none focus:ring-1 focus:ring-nexus-accent-mint"
        >
          <option value="">All Actions</option>
          {actions.map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
        </select>
      </div>

      <NexusCard variant="glass" hover={false}>
        <div className="divide-y divide-white/5">
          {logs.length === 0 ? (
            <div className="p-12 text-center text-nexus-text-tertiary text-sm">No audit logs found</div>
          ) : (
            logs.map((log) => (
              <motion.div
                key={log.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="px-6 py-3 flex items-center justify-between hover:bg-nexus-bg-tertiary/20 transition-colors"
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <NexusBadge variant={ACTION_COLORS[log.action] || 'default'}>
                    {log.action?.replace(/_/g, ' ')}
                  </NexusBadge>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-nexus-text-primary truncate">
                      <span className="text-nexus-text-secondary">{log.actor_type}:</span> {log.actor_id?.slice(0, 8)}...
                      {log.target_type && <span className="text-nexus-text-tertiary"> on {log.target_type}</span>}
                    </p>
                    {log.details && <p className="text-xs text-nexus-text-tertiary truncate mt-0.5">{log.details}</p>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <p className="text-xs text-nexus-text-tertiary">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                  </p>
                  {log.tenant_id && <p className="text-xs text-nexus-text-tertiary">tenant: {log.tenant_id.slice(0, 8)}...</p>}
                </div>
              </motion.div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-3 flex items-center justify-between border-t border-white/5">
            <p className="text-xs text-nexus-text-tertiary">Page {page + 1} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-1.5 rounded-lg bg-nexus-bg-tertiary border border-white/5 disabled:opacity-30 hover:bg-nexus-bg-tertiary/50 transition-colors"
              >
                <ChevronLeft size={14} className="text-nexus-text-secondary" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="p-1.5 rounded-lg bg-nexus-bg-tertiary border border-white/5 disabled:opacity-30 hover:bg-nexus-bg-tertiary/50 transition-colors"
              >
                <ChevronRight size={14} className="text-nexus-text-secondary" />
              </button>
            </div>
          </div>
        )}
      </NexusCard>
    </div>
  );
};

export default AdminAuditLogs;
